import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  type AutocompleteRenderOptionState,
  Box,
  CircularProgress,
  createFilterOptions,
  type FilterOptionsState,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { DeepKeys } from '@tanstack/react-table';
import { Field, useFormikContext } from 'formik';
import { camelCase, get, has } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type OptionType<T> = { _id: string | number } & T;

// Internal sentinel option used when no results are found
type SentinelOption = { _id: string; label?: string; __isAddNew?: boolean };

// The effective option type the Autocomplete will receive
type AnyOption<T> = OptionType<T> | SentinelOption;

// Supported MUI Autocomplete sizes
type AutocompleteSize = 'small' | 'medium';

// Type guard to detect sentinel (non-data) options
function isSentinelOption(option: unknown): option is SentinelOption {
  return (
    !!option &&
    typeof option === 'object' &&
    ('__isAddNew' in (option as Record<string, unknown>) ||
      (option as { _id?: unknown })._id === '__add__')
  );
}

export type InputTypeaheadActions = Record<
  string,
  (payload: { inputValue: string }) => void
>;

export interface InputTypeaheadProps<T> {
  label: string;
  /**
   * Text shown in the empty field. Defaults to `label`, which reads as a
   * noun ("Hunter") — pass a prompt when the field is a SEARCH and the label
   * alone would suggest a plain text entry ("Search for a hunter…").
   */
  placeholder?: string;
  options: AnyOption<T>[];
  idKey?: string;
  labelKey?: DeepKeys<T>;
  disabled?: boolean;
  required?: boolean;
  showLabel?: boolean;
  nameOverride?: string;
  multiple?: boolean;
  allowNew?: boolean;
  minSelection?: number;
  maxSelection?: number;
  addNoResultsOptionLabel?: string;
  actions?: InputTypeaheadActions;
  noResultsActionKey?: string;
  size?: AutocompleteSize;
  onInputChange?: (value: string) => void;
  isLoading?: boolean;
  hint?: string;
  autoFocus?: boolean;
  getOptionDescription?: (option: OptionType<T>) => React.ReactNode;
}

export function InputTypeahead<T extends string | Record<string, unknown>>({
  label,
  placeholder = undefined,
  disabled = false,
  idKey = 'id',
  labelKey = undefined,
  required = false,
  options,
  nameOverride = undefined,
  multiple = false,
  showLabel = true,
  allowNew = false,
  minSelection = undefined,
  maxSelection = undefined,
  addNoResultsOptionLabel = undefined,
  actions = undefined,
  noResultsActionKey = 'addNew',
  size = 'medium',
  onInputChange = undefined,
  isLoading = false,
  hint = undefined,
  autoFocus = false,
  getOptionDescription = undefined,
}: InputTypeaheadProps<T>) {
  const { values, errors, setFieldValue, setFieldTouched } =
    useFormikContext<Record<string, unknown>>();

  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  // The bare `autoFocus` attribute fires before MUI's Dialog FocusTrap settles
  // focus after its open transition, so it doesn't stick. Focus on the next
  // frame instead, once the trap has finished.
  useEffect(() => {
    if (!autoFocus) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const validate = useCallback(
    (value: unknown) => {
      if (multiple && Array.isArray(value)) {
        if (required && (!value || value.length === 0)) {
          return `${label} is required.`;
        }
        if (minSelection && value.length < minSelection) {
          return `${label} must have at least ${minSelection} selection${minSelection > 1 ? 's' : ''}.`;
        }
        if (maxSelection && value.length > maxSelection) {
          return `${label} can have at most ${maxSelection} selection${maxSelection > 1 ? 's' : ''}.`;
        }
      } else if (
        required &&
        (!value || value === null || value === undefined)
      ) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required, multiple, minSelection, maxSelection],
  );

  const isInvalid = useMemo(
    () => has(errors, name) && get(errors, name) !== '',
    [errors, name],
  );

  const currentValue = useMemo(() => {
    const fieldValue = get(values, name);
    if (multiple) {
      return Array.isArray(fieldValue) ? fieldValue : [];
    }
    return fieldValue || null;
  }, [values, name, multiple]);

  const getOptionLabel = useCallback(
    (option: AnyOption<T> | string) => {
      if (typeof option === 'string') return option;
      if (option && 'label' in option && option.label) {
        return option.label;
      }
      if (labelKey) {
        return get(option as OptionType<T>, labelKey as string) || '';
      }
      return (option as OptionType<T>)._id?.toString() || '';
    },
    [labelKey],
  );

  const getOptionValue = useCallback(
    (option: OptionType<T>) => get(option, idKey) || option._id,
    [idKey],
  );

  const isOptionEqualToValue = useCallback(
    (option: AnyOption<T>, value: AnyOption<T>) => {
      if (isSentinelOption(option) || isSentinelOption(value)) return false;
      return (
        getOptionValue(option as OptionType<T>) ===
        getOptionValue(value as OptionType<T>)
      );
    },
    [getOptionValue],
  );

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent,
      newValue: (string | OptionType<T>)[] | string | OptionType<T> | null,
    ) => {
      // Intercept sentinel option in single-select mode
      if (!multiple && newValue && typeof newValue === 'object') {
        const maybeObj = newValue as any;
        if (maybeObj.__isAddNew || maybeObj._id === '__add__') {
          const fn = actions?.[noResultsActionKey];
          if (fn) fn({ inputValue });
          return;
        }
      }

      let processedValue: unknown;

      if (multiple) {
        processedValue = Array.isArray(newValue)
          ? newValue.map((item) =>
              typeof item === 'string' ? item : getOptionValue(item),
            )
          : [];
      } else {
        processedValue =
          newValue === null || typeof newValue === 'string'
            ? newValue
            : getOptionValue(newValue as OptionType<T>);
      }

      setFieldValue(name, processedValue);
    },
    [
      multiple,
      setFieldValue,
      name,
      actions,
      noResultsActionKey,
      inputValue,
      getOptionValue,
    ],
  );

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  // The last option we successfully resolved for a single-select value. MUI
  // renders the input's label from the `value` object it is handed, so without
  // this a selection that drops out of `options` renders as an empty input over
  // a value that is still set. `options` churns constantly on a server-searched
  // picker (every keystroke replaces the page), and the selection frequently
  // isn't on the current page at all, so label resolution must not depend on it.
  const lastResolvedOption = useRef<{
    value: unknown;
    option: OptionType<T>;
  } | null>(null);

  const selectedOptions = useMemo(() => {
    if (!currentValue) return multiple ? [] : null;

    if (multiple) {
      return Array.isArray(currentValue)
        ? options.filter(
            (option) =>
              !isSentinelOption(option) &&
              currentValue.includes(getOptionValue(option as OptionType<T>)),
          )
        : [];
    }

    // Find the option in the current options list
    const foundOption = options.find(
      (option) =>
        !isSentinelOption(option) &&
        getOptionValue(option as OptionType<T>) === currentValue,
    );

    if (foundOption) {
      lastResolvedOption.current = {
        value: currentValue,
        option: foundOption as OptionType<T>,
      };
      return foundOption;
    }

    // Absent from the current page: fall back to the last option resolved for
    // *this* value. Keyed on the value so a new selection never inherits the
    // previous one's label.
    if (lastResolvedOption.current?.value === currentValue) {
      return lastResolvedOption.current.option;
    }

    return null;
  }, [currentValue, multiple, options, getOptionValue]);

  return (
    <FormControl fullWidth error={isInvalid}>
      <Field
        as={Autocomplete}
        id={name}
        name={name}
        validate={validate}
        disabled={disabled}
        multiple={multiple}
        freeSolo={allowNew}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        onBlur={handleBlur}
        getOptionLabel={getOptionLabel}
        getOptionKey={(option: AnyOption<T>) => {
          if (typeof option === 'string') return option;
          return option._id?.toString() || '';
        }}
        isOptionEqualToValue={isOptionEqualToValue}
        onInputChange={(
          _e: React.SyntheticEvent,
          val: string,
          reason: string,
        ) => {
          setInputValue(val);
          // Notify the parent on input and clear, and whenever MUI empties the
          // input of its own accord. A server-searched picker keeps its term in
          // parent state, and MUI empties the input through `resetInputValue`
          // on a value change (reason 'reset') and on blur (reason 'blur') —
          // neither of which was forwarded, so the term stayed stuck on
          // whatever was half-typed while the input showed nothing, and the
          // parent kept querying and filtering for it. The reset that follows a
          // *selection* carries the chosen option's label rather than '', so it
          // is still ignored.
          const emptiedByComponent = val === '';
          if (reason === 'input' || reason === 'clear' || emptiedByComponent) {
            onInputChange?.(val);
          }
        }}
        size={size}
        loading={isLoading}
        loadingText="Searching..."
        filterOptions={(
          opts: AnyOption<T>[],
          params: FilterOptionsState<AnyOption<T>>,
        ) => {
          // If we're using external search with loading indicator,
          // don't apply default filtering - options are pre-filtered
          if (
            onInputChange &&
            params.inputValue &&
            params.inputValue.length >= 2
          ) {
            return opts;
          }

          const defaultFilter = createFilterOptions<AnyOption<T>>();
          const filtered = defaultFilter(opts, params);

          if (
            addNoResultsOptionLabel &&
            params.inputValue &&
            params.inputValue.trim() !== '' &&
            filtered.length === 0
          ) {
            const sentinel: SentinelOption = {
              _id: '__add__',
              label: addNoResultsOptionLabel,
              __isAddNew: true,
            };
            // Return the single sentinel option to render "Add New …"
            return [sentinel as AnyOption<T>];
          }

          // Otherwise, return the filtered results
          return filtered as AnyOption<T>[];
        }}
        renderOption={
          getOptionDescription
            ? (
                props: React.HTMLAttributes<HTMLLIElement>,
                option: AnyOption<T>,
                _state: AutocompleteRenderOptionState,
              ) => {
                // Skip description rendering for sentinel options
                if (isSentinelOption(option)) {
                  return (
                    <li {...props}>
                      <Box sx={{ width: '100%' }}>{getOptionLabel(option)}</Box>
                    </li>
                  );
                }

                const description = getOptionDescription(
                  option as OptionType<T>,
                );
                const optionLabel = getOptionLabel(option);

                return (
                  <li {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1">{optionLabel}</Typography>
                      {description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }
            : undefined
        }
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            inputRef={inputRef}
            label={showLabel ? label : undefined}
            placeholder={placeholder ?? label}
            // For multi-select the inner input never holds the value (chips
            // do), so MUI's native `required` attribute would always trigger
            // the browser's "Please fill out this field" popup even after a
            // chip is selected. Keep the asterisk via `InputLabelProps` and
            // rely on Formik for validation.
            required={multiple ? false : required}
            InputLabelProps={
              multiple && required ? { required: true } : undefined
            }
            error={isInvalid}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              ...(isLoading && {
                endAdornment: (
                  <>
                    <CircularProgress color="inherit" size={20} />
                    {params.InputProps.endAdornment}
                  </>
                ),
              }),
            }}
          />
        )}
      />
      {(isInvalid || hint) && (
        <FormHelperText error={isInvalid}>
          {isInvalid ? String(get(errors, name)) : hint}
        </FormHelperText>
      )}
    </FormControl>
  );
}
